import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { Sheet, SheetContent } from '@savvy-kit/components/sheet';

import type { TrackedParentElementAddedProps } from '@savvy/deprecated-shared/tracking/parent-element-view-context';
import { ParentElementViewProvider } from '@savvy/deprecated-shared/tracking/parent-element-view-context';
import { useTrackElementView } from '@savvy/deprecated-shared/tracking/use-track-element-view';

export type TrackedSheetProps = TrackedParentElementAddedProps &
  Omit<ComponentProps<typeof Sheet>, 'open' | 'onOpenChange' | 'children'> & {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly children: ReactNode;
    readonly size?: ComponentProps<typeof SheetContent>['size'];
    readonly toolbar?: ComponentProps<typeof SheetContent>['toolbar'];
    readonly className?: string;
    readonly overlayClassName?: string;
    readonly portalContainer?: ComponentProps<typeof SheetContent>['portalContainer'];
    readonly initialFocus?: ComponentProps<typeof SheetContent>['initialFocus'];
  };

// A Sheet is a Dialog variant (same base-ui Dialog.Root under the hood), so it's tracked as
// parentElementType="dialog" — no separate "sheet" analytics type needed.
export function TrackedSheet({
  elementName,
  open,
  onOpenChange,
  children,
  size,
  toolbar,
  className,
  overlayClassName,
  portalContainer,
  initialFocus,
  ...sheetRootProps
}: TrackedSheetProps) {
  const onElementHidden = useTrackElementView({ elementName, elementType: 'dialog', isVisible: open });

  // Record view-duration when an open sheet goes away — via open→false OR via
  // unmount (many consumers render `{show && <TrackedSheet open .../>}` and close
  // by unmounting, so `open` never flips). Cleanup only runs for an `open` render,
  // so a consumer-vetoed close (open stays true) isn't counted.
  const onElementHiddenRef = useRef(onElementHidden);
  onElementHiddenRef.current = onElementHidden;
  useEffect(() => {
    if (!open) return;
    return () => onElementHiddenRef.current();
  }, [open]);

  return (
    <Sheet {...sheetRootProps} open={open} onOpenChange={onOpenChange}>
      <SheetContent
        size={size}
        toolbar={toolbar}
        className={className}
        overlayClassName={overlayClassName}
        portalContainer={portalContainer}
        initialFocus={initialFocus}
      >
        <ParentElementViewProvider parentElementName={elementName} parentElementType="dialog">
          {children}
        </ParentElementViewProvider>
      </SheetContent>
    </Sheet>
  );
}
